import {
  AIIdeaInput,
  AIIdeaOutput,
  BusinessIdea,
  BusinessIdeaKit,
  SurveyQuestion,
  TaskItem,
  AIServiceError,
  AIErrorCode
} from '../../types/ai';
import { PromptBuilder } from './promptBuilder';
import { ResponseParser } from './responseParser';
import { FallbackProvider } from './fallbackProvider';
import { AIApiClient } from './apiClient';
import { AIServiceErrorHandler } from './errorHandler';

/**
 * AI Idea Generator Service
 * Main orchestration service for AI-powered business idea generation
 */
export class AIIdeaGeneratorService {
  private static readonly providerConfigs = {
    gemini: {
      timeout: 30000,
      retryAttempts: 2,
      fallbackEnabled: true
    },
    deepseek: {
      timeout: 30000,
      retryAttempts: 2,
      fallbackEnabled: true
    }
  };

  private static getConfig() {
    const provider = AIApiClient.getProvider();
    return this.providerConfigs[provider];
  }

  /**
   * Generate business ideas using AI with enhanced error handling and fallback
   */
  static async generateIdeas(
    input: AIIdeaInput, 
    language: string = 'en',
    targetMarket: string = 'Global',
    detailLevel: 'basic' | 'standard' | 'detailed' = 'standard'
  ): Promise<AIIdeaOutput> {
    this.validateInput(input);

    return AIServiceErrorHandler.handleAIFailure(
      async () => {
        const startTime = Date.now();
        const aiIdeas = await this.generateIdeasWithAI(input, language, targetMarket, detailLevel);
        const processingTime = Date.now() - startTime;
        
        this.storeLanguageMetadata(aiIdeas, language);
        
        return {
          ideas: aiIdeas,
          confidence: 0.95,
          processingTime,
          generatedAt: new Date()
        };
      },
      () => {
        if (this.getConfig().fallbackEnabled) {
          const fallbackIdeas = FallbackProvider.getFallbackBusinessKits(input, language);
          this.storeLanguageMetadata(fallbackIdeas, language);
          
          return {
            ideas: fallbackIdeas,
            confidence: 0.6,
            processingTime: 0,
            generatedAt: new Date()
          };
        }
        throw this.createAIServiceError(AIErrorCode.GENERATION_FAILED, 'Failed to generate ideas and fallback disabled');
      },
      'idea-generation',
      {
        maxRetries: this.getConfig().retryAttempts + 1,
        enableRateLimit: true,
        rateLimitKey: 'idea-generation'
      }
    );
  }

  /**
   * Generate validation questions for a business idea with enhanced error handling
   */
  static async generateValidationQuestions(idea: BusinessIdea, language: string = 'en'): Promise<SurveyQuestion[]> {
    return AIServiceErrorHandler.handleAIFailure(
      async () => {
        const prompt = PromptBuilder.createValidationQuestionsPrompt(idea, language);
        const response = await AIApiClient.callAI(prompt, 'validation-questions');
        return ResponseParser.parseValidationQuestions(response);
      },
      () => FallbackProvider.getFallbackValidationQuestions(idea),
      'validation-questions',
      {
        maxRetries: 2,
        enableRateLimit: true,
        rateLimitKey: 'validation-questions'
      }
    );
  }

  /**
   * Generate task checklist for MVP development with enhanced error handling
   */
  static async generateTaskChecklist(idea: BusinessIdea, language: string = 'en'): Promise<TaskItem[]> {
    return AIServiceErrorHandler.handleAIFailure(
      async () => {
        const prompt = PromptBuilder.createTaskChecklistPrompt(idea, language);
        const response = await AIApiClient.callAI(prompt, 'task-checklist');
        return ResponseParser.parseTaskChecklist(response);
      },
      () => FallbackProvider.getFallbackTaskChecklist(idea),
      'task-checklist',
      {
        maxRetries: 2,
        enableRateLimit: true,
        rateLimitKey: 'task-checklist'
      }
    );
  }

  /**
   * Generate ideas using AI with comprehensive token limit handling
   */
  private static async generateIdeasWithAI(
    input: AIIdeaInput, 
    language: string = 'en',
    targetMarket: string = 'Global',
    detailLevel: 'basic' | 'standard' | 'detailed' = 'standard'
  ): Promise<BusinessIdeaKit[]> {
    // Prepare options for trends integration
    const apiOptions = {
      language,
      skills: input.skills,
      interests: input.interests,
      domains: [], // Not in AIIdeaInput, can be added later if needed
      industry: input.problemType, // Use problemType as industry context
      includeTrends: true
    };

    // Try with full business kit prompt first
    try {
      console.log(`🎯 Attempting full business kit generation (detail level: ${detailLevel})`);
      const prompt = PromptBuilder.createBusinessKitPrompt(input, language, targetMarket, detailLevel);
      const response = await AIApiClient.callAI(prompt, 'generate-ideas', apiOptions);
      const ideas = ResponseParser.parseBusinessIdeaKit(response);
      console.log(`✅ Successfully generated ${ideas.length} business kits with full prompt`);
      return ideas;
    } catch (error: any) {
      // Check if error is token-related
      if (AIServiceErrorHandler.isTokenLimitError(error)) {
        console.log(`⚠️ Token limit detected, retrying with short prompt (detail level: ${detailLevel})`);
        
        try {
          // Retry with shorter prompt
          const shortPrompt = PromptBuilder.createShortBusinessKitPrompt(input, language, targetMarket, detailLevel);
          const response = await AIApiClient.callAI(shortPrompt, 'generate-ideas', apiOptions);
          const ideas = ResponseParser.parseBusinessIdeaKit(response);
          console.log(`✅ Successfully generated ${ideas.length} business kits with short prompt`);
          return ideas;
        } catch (shortPromptError: any) {
          // If short prompt also fails with token limit, try with basic detail level
          if (AIServiceErrorHandler.isTokenLimitError(shortPromptError) && detailLevel !== 'basic') {
            console.log(`⚠️ Short prompt also hit token limit, retrying with basic detail level`);
            
            try {
              const basicPrompt = PromptBuilder.createShortBusinessKitPrompt(input, language, targetMarket, 'basic');
              const response = await AIApiClient.callAI(basicPrompt, 'generate-ideas', apiOptions);
              const ideas = ResponseParser.parseBusinessIdeaKit(response);
              console.log(`✅ Successfully generated ${ideas.length} business kits with basic detail level`);
              return ideas;
            } catch (basicError: any) {
              console.error(`❌ All token limit retry attempts failed:`, basicError);
              throw basicError;
            }
          }
          
          // If not a token limit error, throw the original error
          throw shortPromptError;
        }
      }
      
      // If not a token limit error, throw the original error
      throw error;
    }
  }

  /**
   * Store language metadata in localStorage for each generated idea
   */
  private static storeLanguageMetadata(ideas: (BusinessIdea | BusinessIdeaKit)[], language: string): void {
    ideas.forEach((idea, index) => {
      const ideaId = `${idea.title.toLowerCase().replace(/\s+/g, '-')}-${Date.now()}-${index}`;
      try {
        localStorage.setItem(`content_language_idea_${ideaId}`, language);
        console.log(`✅ Stored language metadata for idea: ${ideaId} (${language})`);
      } catch (error) {
        console.warn(`⚠️ Failed to store language metadata for idea ${ideaId}:`, error);
      }
    });
  }

  /**
   * Validate input parameters
   */
  private static validateInput(input: AIIdeaInput): void {
    if (!input.skills || input.skills.length === 0) {
      throw this.createAIServiceError(AIErrorCode.INVALID_INPUT, 'Skills are required');
    }

    if (!input.interests || input.interests.length === 0) {
      throw this.createAIServiceError(AIErrorCode.INVALID_INPUT, 'Interests are required');
    }

    if (!input.problemType || input.problemType.trim() === '') {
      throw this.createAIServiceError(AIErrorCode.INVALID_INPUT, 'Problem type is required');
    }
  }

  /**
   * Create standardized AI service error
   */
  private static createAIServiceError(code: AIErrorCode, message: string, originalError?: any): AIServiceError {
    return {
      code,
      message,
      context: originalError?.message || 'Unknown error',
      timestamp: new Date()
    };
  }

  /**
   * Get current fallback models configuration
   */
  static getFallbackModels(): string[] {
    return AIApiClient.getFallbackModels();
  }
}

// Re-export error handling utilities for backward compatibility
export { AIServiceErrorHandler, AIErrorMessages, getAIErrorMessage } from './errorHandler';
